import { getDefaultConfig, type Wallet, type WalletDetailsParams } from '@rainbow-me/rainbowkit';
import type { EIP1193Provider } from 'viem';
import { Config, createConnector, fallback, http } from 'wagmi';
import { mainnet, sepolia } from 'wagmi/chains';
import { injected } from 'wagmi/connectors';

import {
  base,
  metaMaskWallet,
  rabbyWallet,
  rainbowWallet,
  walletConnectWallet,
} from '@/app/providers/app/rainbowkitWallets';
import { RPC_NODES } from '@/app/shared/constants/web3/constants';

const PROJECT_ID = '427e30b4efbb68e836c169bd764b35a0';

type EthereumProvider = EIP1193Provider & {
  isMetaMask?: boolean;
  isRabby?: boolean;
  isRainbow?: boolean;
  providers?: Array<
    EIP1193Provider & {
      isMetaMask?: boolean;
      isRabby?: boolean;
      isRainbow?: boolean;
    }
  >;
};

type WindowWithEthereum = Window & { ethereum?: EthereumProvider };

const getEthereum = (): EthereumProvider | undefined => {
  if (typeof window === 'undefined') return undefined;
  return (window as WindowWithEthereum).ethereum;
};

const hasInjectedEthereum = (): boolean => Boolean(getEthereum());

const isMobileDevice = (): boolean =>
  typeof navigator !== 'undefined' && /Android|iPhone|iPod|iPad/i.test(navigator.userAgent);

const providerList = (): EthereumProvider[] => {
  const ethereum = getEthereum();
  if (!ethereum) return [];
  if (ethereum.providers?.length) return ethereum.providers;
  return [ethereum];
};

const hasRabbyInjected = (): boolean => providerList().some((provider) => provider.isRabby);

const hasRainbowInjected = (): boolean => providerList().some((provider) => provider.isRainbow);

const hasMetaMaskInjected = (): boolean =>
  providerList().some((provider) => provider.isMetaMask && !provider.isRabby && !provider.isRainbow);

const createInjectedConnector =
  (id: string, name: string) => (walletDetails: WalletDetailsParams) =>
    createConnector((config) => ({
      ...injected({
        target: () => {
          const provider = getEthereum();
          if (!provider) return undefined;
          return { id, name, provider: provider as EIP1193Provider };
        },
      })(config),
      ...walletDetails,
    }));

const preferInjectedOnMobile = (wallet: Wallet): Wallet => {
  if (!(isMobileDevice() && hasInjectedEthereum())) return wallet;

  return {
    ...wallet,
    installed: true,
    mobile: undefined,
    qrCode: undefined,
    createConnector: createInjectedConnector(wallet.id, wallet.name),
  };
};

const forMobilePopular = (wallet: Wallet, isInjectedOnDesktop: () => boolean): Wallet => ({
  ...wallet,
  // Mobile: always show popular wallets. Desktop: only if that extension is injected.
  hidden: () => !isMobileDevice() && !isInjectedOnDesktop(),
});

const metaMaskConfigured = ((...args: Parameters<typeof metaMaskWallet>) => {
  const wallet = preferInjectedOnMobile(metaMaskWallet(...args));
  return forMobilePopular(wallet, hasMetaMaskInjected);
}) as typeof metaMaskWallet;

const rainbowConfigured = ((...args: Parameters<typeof rainbowWallet>) => {
  const wallet = preferInjectedOnMobile(rainbowWallet(...args));
  return forMobilePopular(wallet, hasRainbowInjected);
}) as typeof rainbowWallet;

const rabbyConfigured = ((...args: Parameters<typeof rabbyWallet>) => {
  const wallet = preferInjectedOnMobile(rabbyWallet(...args));
  return forMobilePopular(wallet, hasRabbyInjected);
}) as typeof rabbyWallet;

const baseConfigured = ((...args: Parameters<typeof base>) => {
  const wallet = base(...args);
  return forMobilePopular(wallet, () => false);
}) as typeof base;

export const config = getDefaultConfig({
  appName: 'mezzanine',
  projectId: PROJECT_ID,
  // pre-dev / develop: Sepolia first (testnet default); mainnet kept for vault lookups
  chains: [sepolia, mainnet],
  multiInjectedProviderDiscovery: true,
  transports: {
    [sepolia.id]: fallback(RPC_NODES[sepolia.id].map((url) => http(url))),
    [mainnet.id]: fallback(RPC_NODES[mainnet.id].map((url) => http(url))),
  },
  wallets: [
    {
      groupName: 'Popular',
      wallets: [metaMaskConfigured, rainbowConfigured, baseConfigured, rabbyConfigured, walletConnectWallet],
    },
  ],
}) as Config;
